// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type checks for this
// file, exactly as StageTrendChart.tsx and components/common/MonthlyBreakdownChart.tsx do.
import React from 'react';
import { Area, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CHART_AXIS_TICK, TOOLTIP_CARD, gradientDefs } from './trackerKit';

/**
 * Actual against target, one point per month.
 *
 * The same line as Trends' "Stages that moved" (StageTrendChart; founder, 5 Oct 2026): a monotone
 * 2px stroke with no resting dots, a gradient fill under it from the house kit (chartGradient
 * 'area'), dashed #344155 grid, #8892b0 axes and the dark tooltip card. The target is a dashed grey
 * line, drawn only for months that HAVE a target (a null point leaves a gap rather than dropping
 * the line to zero, because "no target" is not a target of nothing).
 *
 * Pure presentation: every figure arrives computed. `format` turns a value into the axis and
 * tooltip text, so the same chart serves dollars (cents in, money() out) and deal counts.
 */
export interface MovementTrendPoint {
  label: string;
  actual: number;
  target: number | null;
  partial: boolean;
}

const MovementTrendChart = ({ data, colour, stageLabel, format, showTarget }) => {
  const gradientId = `movement-trend-${stageLabel.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload;
    return (
      <div className={TOOLTIP_CARD}>
        <p className="mb-1 font-bold">
          {label}
          {row.partial ? ' (to the snapshot date)' : ''}
        </p>
        <p style={{ color: colour }}>
          {stageLabel}: <span className="tabular-nums">{format(row.actual)}</span>
        </p>
        {showTarget && (
          <p className="text-gray-400">
            Target: <span className="tabular-nums">{row.target === null ? 'not set' : format(row.target)}</span>
          </p>
        )}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        {gradientDefs(gradientId, colour)}
        <XAxis dataKey="label" stroke="#8892b0" tick={CHART_AXIS_TICK} tickLine={false} interval="preserveStartEnd" minTickGap={20} />
        <YAxis stroke="#8892b0" tick={CHART_AXIS_TICK} width={64} tickLine={false} tickFormatter={(v) => format(v)} allowDecimals={false} />
        <CartesianGrid strokeDasharray="3 3" stroke="#344155" opacity={0.3} />
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperStyle={{ fontSize: 12, color: '#9ca3af' }} />
        <Area
          type="monotone"
          dataKey="actual"
          name={`${stageLabel}, actual`}
          stroke={colour}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          dot={false}
          activeDot={{ r: 5, fill: colour }}
          isAnimationActive={false}
        />
        {showTarget && (
          <Line
            type="monotone"
            dataKey="target"
            name="Target"
            stroke="#9ca3af"
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={false}
            activeDot={{ r: 4, fill: '#9ca3af' }}
            connectNulls={false}
            isAnimationActive={false}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default MovementTrendChart;
